// chat/photo.tsx — the "Snap code" entry point, ported from Oriora Plant Diary's photo pattern.
//
// The user photographs a code snippet / error message / architecture diagram (a colleague's screen,
// a book, a slide). readDownscaledPhoto compresses it ON-DEVICE; App.tsx then sends the image to the
// brain to read the code/text out of it (OCR/vision) and feeds that text straight into the app's
// existing on-device codebase search. One button gives iOS the native "Take Photo / Photo Library"
// sheet — camera and library both — via accept="image/*".

import { useRef } from "react";
import type { ChangeEvent } from "react";
import { Camera } from "lucide-react";
import { readDownscaledPhoto } from "../photo";

/** Labeled "Snap code" button — sits under the Ask/Search control. On pick it hands back a
 *  downscaled JPEG data-URL; the caller runs the extract → search flow. */
export function SnapCodeButton({
  onPhoto,
  disabled,
  busy,
}: {
  onPhoto: (dataUrl: string) => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);

  async function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked twice in a row
    if (!file) return;
    try { onPhoto(await readDownscaledPhoto(file)); } catch { /* caller surfaces nothing changed */ }
  }

  return (
    <>
      {/* accept=image/* gives iOS the native "Take Photo / Photo Library" sheet — one button, both. */}
      <input ref={ref} type="file" accept="image/*" onChange={(e) => void onPicked(e)} style={{ display: "none" }} />
      <button
        type="button"
        className="btn-ghost row"
        style={{ gap: "0.4rem", alignItems: "center", fontWeight: 600, border: "1px solid var(--border-hairline)", flexShrink: 0 }}
        aria-label="Snap code — photograph a snippet, error or diagram to search your codebase"
        title="Photograph code, an error or a diagram — the AI reads it and searches your codebase"
        disabled={disabled || busy}
        onClick={() => ref.current?.click()}
      >
        <Camera size={15} style={{ color: "var(--accent)" }} />
        {busy ? "Reading photo…" : "Snap code"}
      </button>
    </>
  );
}
